/**
 * hq-combo-builder
 *
 * The bundles + closure set (snippets/hq-combo-builder.liquid).
 *
 * Interaction model
 * -----------------
 * shade (once)  ->  texture (once)  ->  how many bundles  ->  tap that many
 * lengths out of one grid (a length tapped twice is two bundles of it)  ->
 * which closure or frontal, and its length.
 *
 * Nothing is pre-filled: the button says how many lengths are still to choose
 * until the set is complete. The closure length follows the shortest bundle
 * until the shopper picks one; after that it is theirs, and switching to a
 * closure that does not come in it moves to the nearest length it does.
 *
 * A closure that is not made in the chosen shade or texture (the 2x6 and 5x5
 * closures have no Lux textures) is disabled, and if it was the one selected
 * the first closure that is made in it takes its place, with a note saying so.
 *
 * Pricing is the plain sum of the real variants (assets/hq-combo-logic.js,
 * priceSet). Nothing here discounts anything, and the cart lines carry
 * _combo_id rather than _bundle_id, so the checkout's bundle tier cannot
 * touch them either. Page total = checkout total.
 *
 * Every bundle its own shade (2026-11-03, the owner's request, as on the
 * invisible clip-ins): each line of "Your set" has Change, which opens that
 * bundle's own shade and length (or the closure's own shade) inside the box.
 * A bundle or closure left alone follows the shade above. One box for checking
 * and changing the set, so the page does not show it twice. A pick is
 * { len, shade } (assets/hq-combo-logic.js,
 * tapPick): shade null = follows the set. A bundle in another shade is the
 * listing that sells that shade (pickVariant), in the set's texture.
 *
 * Shade and texture buttons are tagged data-cb-shade / data-cb-texture, NOT
 * data-shade / data-texture: the host's gallery filter (hq-pdp-clip-ins.js,
 * filterGallery) hides every [data-shade] element in the section that does not
 * match the chosen shade, so it would hide all but one of the tiles.
 *
 * Host page: the hq-pdp-clip-ins section. This element tells it which shade is
 * chosen (filterByShade, for the gallery) and writes the set total into its
 * sticky bar; the bar's button calls addToCart() here
 * (assets/hq-pdp-clip-ins.js, bindSticky).
 *
 * Price, summary and button (2026-10-30, from the owner's notes)
 * - On phones the sticky bar is on screen from the start (data-bar), not
 *   only once the button has scrolled away: the price sits at the bottom and
 *   changes as the shopper chooses. Until every length is chosen it reads
 *   "From $X" (hq-combo-logic.js, priceEstimate). The bar is made over here
 *   (setupBar, class hq-cb-bar) so the section and its other pages are
 *   untouched.
 * - "Your set" lists each bundle and the closure on its own line with its
 *   price, then shipping and the total, and its button goes straight to
 *   checkout (data-checkout); "or add to cart" under it still goes to the cart.
 */
class HqComboBuilder extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.logic = window.HqComboLogic;
    this.pricing = window.HqBundlePricing;
    if (!this.logic || !this.pricing) {
      /* Deferred scripts run in order, so this only happens if one failed to
         load. Better a disabled button than a set added at the wrong price. */
      console.error('[hq-combo-builder] helper scripts missing');
      return;
    }

    this.config = this.readJSON('[data-config]') || {};
    this.t = this.readJSON('[data-text]') || {};
    this.money = this.pricing.parseMoneySample(this.dataset.moneySample);
    this.cartRedirect = this.dataset.cartRedirect !== 'false';
    this.toCheckout = this.dataset.checkout === 'true';
    this.shipOpts = {
      min: parseInt(this.dataset.freeShip, 10) || 0,
      country: this.dataset.country || '',
      currency: this.dataset.currency || '',
      shopCurrency: this.dataset.shopCurrency || '',
    };
    this.pdp = this.closest('hq2-pdp');

    const q = (sel) => this.querySelector(sel);
    this.el = {
      shades: q('[data-shades]'),
      shadeName: q('[data-shade-name]'),
      textureGroup: q('[data-texture-group]'),
      textures: q('[data-textures]'),
      textureName: q('[data-texture-name]'),
      counts: [...this.querySelectorAll('[data-count]')],
      prompt: q('[data-length-prompt]'),
      counter: q('[data-counter]'),
      lengths: q('[data-lengths]'),
      setHint: q('[data-set-hint]'),
      clear: q('[data-clear]'),
      lengthHint: q('[data-length-hint]'),
      nudge: q('[data-nudge]'),
      types: q('[data-types]'),
      closureLengths: q('[data-closure-lengths]'),
      closureName: q('[data-closure-name]'),
      closureHint: q('[data-closure-hint]'),
      lines: q('[data-lines]'),
      ship: q('[data-ship]'),
      shipLabel: q('[data-ship-label]'),
      shipValue: q('[data-ship-value]'),
      total: q('[data-total]'),
      add: q('[data-add]'),
      addLabel: q('[data-add-label]'),
      addCart: q('[data-add-cart]'),
      status: q('[data-status]'),
    };

    /* Bundle listings: the highlighted bundles (one colour option, eleven
       shades) and single-colour listings (black, 613). Each shade knows which
       listing sells it, and that listing's textures, lengths and prices apply
       while it is chosen (the listing/textures/bundleLengths getters). */
    this.bundles = this.config.bundles || [];
    this.closures = this.config.closures || [];
    this.shades = this.logic.bundleShades(this.bundles);
    /* The single-colour listings (Natural Black #1B, 613) ahead of the
       highlighted shades when the block asks for it (owner, 2026-11-03). The
       first shade is also the one the page opens on. */
    if (this.config.extraFirst) this.shades = this.shades.filter((sh) => sh.product > 0).concat(this.shades.filter((sh) => sh.product === 0));
    this.types = this.logic.closureTypes(this.closures);

    const counts = this.config.counts || {};
    this.count = counts.default || 3;
    this.picks = [];
    this.editing = null;
    /* The closure's own shade id, null = the set's shade above. */
    this.closureOwn = null;
    this.closureEditing = false;
    this.shade = this.shades[0] || null;
    this.texture = this.textures[0] || null;
    this.typeId = this.types.length ? this.types[0].id : null;
    this.closureLengthPicked = null;

    /* Variants per listing: 'b' for the bundles, the closure's index for each closure. */
    this.variants = {};
    this.failed = false;

    this.drawShades();
    this.drawTextures();
    this.bind();
    this.setupBar();
    this.update();
    this.loadAll();

    /* Back from checkout or the cart restores this page from the back/forward
       cache as it was left: busy, the button reading "Going to checkout…".
       Give the shopper their set back, ready to use. */
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted || !this.busy) return;
      this.busy = false;
      this.el.add.classList.remove('is-busy');
      this.renderTotals();
    });
  }

  /**
   * The host's sticky bar, made over for this page (phones only — the CSS
   * hides it from 768px, where "Your set" does its job): on screen from the start
   * (not only once the button has scrolled away), price and what is in the set
   * on the left, Checkout on the right, the return terms under them. The page
   * is padded by the bar's height so it never covers the footer, and the chat
   * button is lifted above it (both through --hq-cb-bar-h).
   */
  setupBar() {
    const bar = this.pdp && this.pdp.querySelector('[data-hq2-sticky]');
    if (!bar || this.dataset.bar !== 'true') return;
    this.bar = bar;
    bar.classList.add('hq-cb-bar');
    const inner = bar.querySelector('.hq2-sticky-inner') || bar;
    const returns = this.querySelector('[data-bar-returns]');
    if (returns) { returns.hidden = false; inner.appendChild(returns); }
    const label = bar.querySelector('[data-hq2-sticky-atc] > span');
    if (label && this.toCheckout) label.textContent = `${this.t.checkout || 'Checkout'} →`;

    const measure = () => {
      const below = parseFloat(getComputedStyle(bar).bottom) || 0;
      document.body.style.setProperty('--hq-cb-bar-h', `${Math.ceil(bar.offsetHeight + below)}px`);
    };
    document.body.classList.add('hq-cb-bar-on');
    measure();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(bar);
    window.addEventListener('resize', measure);
  }

  readJSON(sel) {
    const node = this.querySelector(sel);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { console.error('[hq-combo-builder] bad JSON in', sel, e); return null; }
  }

  fmt(cents) { return this.pricing.formatMoney(cents, this.money); }

  /** A length as people read it, whatever the listing calls it: '16in' and '16"' both show as 16". */
  len(value) {
    const n = this.logic.inches(value);
    return n ? `${n}"` : String(value || '');
  }

  word(n) { return n === 1 ? (this.t.bundle || 'bundle') : (this.t.bundles || 'bundles'); }

  fill(tpl, vars) { return String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m)); }

  /** "⁠Lux deep wave" -> "Lux deep wave", "Champagne-Blonde-Blend" -> "Champagne Blonde Blend". */
  nice(value) { return String(value || '').replace(/[⁠​]/g, '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim(); }

  /* ------------------------------------------------------------------ data */

  /**
   * Every variant of every listing, from the variant feed (250 a page). Two of
   * the listings are past Liquid's 250 cap and product.js stops there too, so
   * the feed is the only complete source; a partial list would make a length
   * look unavailable when it is not.
   */
  feed(url, pages) {
    const reqs = [];
    for (let n = 1; n <= Math.max(1, pages || 1); n++) {
      reqs.push(fetch(`${url}?section_id=hq-variant-feed&page=${n}`, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(`${url} feed page ${n}: ${r.status}`); return r.text(); })
        .then((html) => {
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hq-variant-feed]');
          if (!node) throw new Error(`${url} feed page ${n}: no data`);
          return JSON.parse(node.textContent).v;
        }));
    }
    return Promise.all(reqs).then((pagesOfRows) => pagesOfRows.flat().map((r) => ({
      id: r[0], o: [r[1], r[2], r[3]].map((x) => (x == null ? x : String(x).trim())), p: r[4], a: !!r[5],
    })));
  }

  loadAll() {
    const jobs = [];
    this.bundles.forEach((b, i) => {
      jobs.push(this.feed(b.url, b.pages).then((v) => { this.variants[`b${i}`] = v; this.update(); }));
    });
    this.closures.forEach((c, i) => {
      jobs.push(this.feed(c.url, c.pages).then((v) => { this.variants[`c${i}`] = v; this.update(); }));
    });
    Promise.all(jobs).catch((err) => {
      console.error('[hq-combo-builder]', err);
      this.failed = true;
      this.update();
    });
  }

  get loaded() {
    return this.bundles.every((b, i) => !!this.variants[`b${i}`]) && this.closures.every((c, i) => !!this.variants[`c${i}`]);
  }

  /** The bundle listing that sells the chosen shade. */
  get listing() { return this.bundles[this.shade ? this.shade.product : 0] || this.bundles[0]; }

  get textures() {
    const b = this.listing;
    return b && b.texture > -1 ? (b.options[b.texture] || []) : [];
  }

  get bundleLengths() {
    const b = this.listing;
    return b ? this.logic.sortLengths(b.options[b.length] || []) : [];
  }

  get bundleVariants() { return this.shade ? this.variants[`b${this.shade.product}`] : null; }

  /** The cheapest length on offer in this shade and texture, in cents; null until loaded. */
  cheapestBundle() {
    const prices = this.bundleLengths.map((l) => this.bundleVariant(l)).filter((v) => v && v.a).map((v) => v.p);
    return prices.length ? Math.min(...prices) : null;
  }

  bundleVariant(length) {
    const vs = this.bundleVariants;
    if (!vs || !this.shade) return null;
    const b = this.listing;
    const want = {};
    if (b.color > -1) want[b.color] = this.shade.value;
    want[b.length] = length;
    if (b.texture > -1 && this.texture) want[b.texture] = this.texture;
    return this.logic.findVariant(vs, want);
  }

  /** The shade a bundle is made in: its own, else the set's. */
  shadeOf(pick) {
    return (pick && pick.shade != null && this.shades.find((sh) => sh.id === pick.shade)) || this.shade;
  }

  /**
   * One bundle's real variant: the listing that sells its shade, the set's
   * texture as that listing spells it, its own length by inches ('16in' on the
   * black listing is 16"). Null if that listing does not make it.
   */
  variantIn(sh, len) {
    if (!sh) return null;
    const b = this.bundles[sh.product];
    const vs = this.variants[`b${sh.product}`];
    if (!b || !vs) return null;
    const want = {};
    if (b.color > -1) want[b.color] = sh.value;
    const at = (b.options[b.length] || []).find((l) => this.logic.inches(l) === this.logic.inches(len));
    if (!at) return null;
    want[b.length] = at;
    if (b.texture > -1 && this.texture) {
      const tx = this.logic.sameValue(b.options[b.texture], this.texture);
      if (!tx) return null;
      want[b.texture] = tx;
    }
    return this.logic.findVariant(vs, want);
  }

  pickVariant(pick) { return this.variantIn(this.shadeOf(pick), pick.len); }

  /** The lengths a shade comes in, in the set's texture; null until its listing has loaded. */
  lengthsIn(sh) {
    const b = sh && this.bundles[sh.product];
    if (!b || !this.variants[`b${sh.product}`]) return null;
    return this.logic.sortLengths(b.options[b.length] || []).filter((l) => { const v = this.variantIn(sh, l); return v && v.a; });
  }

  /** Does the listing selling this shade come in the set's texture? */
  makesTexture(sh) {
    const b = sh && this.bundles[sh.product];
    if (!b) return false;
    return !(b.texture > -1 && this.texture) || !!this.logic.sameValue(b.options[b.texture], this.texture);
  }

  /** The closure's shade: its own once the shopper gives it one, else the set's (owner, 2026-11-03). */
  closureShade() {
    return (this.closureOwn != null && this.shades.find((sh) => sh.id === this.closureOwn)) || this.shade;
  }

  /**
   * Everything about one closure choice for the closure's shade (`sh`, by
   * default closureShade()) and the set's texture: whether it is made in
   * them, the lengths it comes in, the length it would be, and that variant.
   */
  closureFor(type, sh = this.closureShade()) {
    const c = this.closures[type.product];
    const vs = this.variants[`c${type.product}`];
    const out = { type, product: c, ok: false, why: '', lengths: [], length: null, variant: null };
    if (!vs || !sh) return out;

    const want = {};
    if (c.color > -1) {
      const v = this.logic.sameValue(c.options[c.color], sh.value);
      if (!v) { out.why = 'shade'; return out; }
      want[c.color] = v;
    }
    if (c.texture > -1 && this.texture) {
      const v = this.logic.sameValue(c.options[c.texture], this.texture);
      if (!v) { out.why = 'texture'; return out; }
      want[c.texture] = v;
    }
    if (c.size > -1 && type.size) want[c.size] = type.size;

    out.lengths = this.logic.sortLengths(c.options[c.length] || []).filter((l) => {
      const w = Object.assign({}, want);
      w[c.length] = l;
      const v = this.logic.findVariant(vs, w);
      return v && v.a;
    });
    if (!out.lengths.length) { out.why = 'stock'; return out; }

    out.length = this.logic.closureLengthFor(this.logic.pickLengths(this.picks), out.lengths, this.closureLengthPicked);
    const w = Object.assign({}, want);
    w[c.length] = out.length;
    out.variant = this.logic.findVariant(vs, w);
    out.ok = !!(out.variant && out.variant.a);
    return out;
  }

  currentClosure() {
    const type = this.types.find((t) => t.id === this.typeId) || this.types[0];
    return type ? this.closureFor(type) : null;
  }

  /* ---------------------------------------------------------------- render */

  drawShades() {
    const tiles = this.config.tiles || [];
    const tileFor = (value) => tiles.find((t) => this.logic.key(t.label) === this.logic.key(value));
    this.el.shades.innerHTML = '';
    this.shades.forEach((sh) => {
      const tile = tileFor(sh.value);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hq2-sw';
      btn.dataset.cbShade = sh.id;
      btn.setAttribute('aria-label', this.nice(sh.value));
      btn.setAttribute('aria-pressed', 'false');
      const span = document.createElement('span');
      span.className = 'hq2-sw-tile';
      if (tile && tile.img) span.style.backgroundImage = `url("${tile.img}")`;
      else if (tile && tile.color) span.style.backgroundColor = tile.color;
      btn.appendChild(span);
      this.el.shades.appendChild(btn);
    });
  }

  pill(text, data, sub) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hq2-pill';
    Object.keys(data).forEach((k) => { btn.dataset[k] = data[k]; });
    btn.setAttribute('aria-pressed', 'false');
    const main = document.createElement('span');
    main.textContent = text;
    btn.appendChild(main);
    if (sub != null) {
      const d = document.createElement('span');
      d.className = 'hq2-pill-delta';
      d.textContent = sub;
      btn.appendChild(d);
    }
    return btn;
  }

  drawTextures() {
    /* Redrawn when the shade moves to another listing: the black and 613
       listings have their own texture lists. */
    this.texturesFor = this.shade ? this.shade.product : 0;
    this.el.textures.innerHTML = '';
    this.el.textureGroup.hidden = this.textures.length < 2;
    if (this.textures.length < 2) return;
    this.textures.forEach((tx) => this.el.textures.appendChild(this.pill(this.nice(tx), { cbTexture: tx })));
  }

  drawLengths() {
    /* Each pill quotes the difference from the cheapest length, the way the
       other product pages do; the cheapest shows nothing. */
    const low = this.cheapestBundle();
    this.el.lengths.innerHTML = '';
    this.bundleLengths.forEach((l) => {
      const v = this.bundleVariant(l);
      /* By inches: a bundle in another shade may be another listing's '16in'. */
      const n = this.picks.filter((p) => this.logic.inches(p.len) === this.logic.inches(l)).length;
      const sub = v && low != null && v.p > low ? `+${this.fmt(v.p - low)}` : null;
      const btn = this.pill(this.len(l), { cbLength: l }, sub);
      btn.classList.add('hq-cb__len');
      btn.setAttribute('aria-pressed', String(n > 0));
      if (this.bundleVariants && (!v || !v.a)) btn.disabled = true;
      if (n > 0) btn.setAttribute('aria-label', `${this.len(l)}, ${n} chosen`);
      /* The filled pill already says "chosen"; the badge only adds news from two up. */
      if (n > 1) {
        const badge = document.createElement('span');
        badge.className = 'hq-cb__badge';
        badge.textContent = `×${n}`;
        badge.setAttribute('aria-hidden', 'true');
        btn.appendChild(badge);
      }
      this.el.lengths.appendChild(btn);
    });

  }

  /** A shade's swatch for a card or the editor: its photo, else its colour. */
  tileFor(value) {
    const tiles = this.config.tiles || [];
    return tiles.find((t) => this.logic.key(t.label) === this.logic.key(value)) || null;
  }

  swatch(value, cls) {
    const span = document.createElement('span');
    span.className = cls || 'hq2-sw-tile';
    const tile = this.tileFor(value);
    if (tile && tile.img) span.style.backgroundImage = `url("${tile.img}")`;
    else if (tile && tile.color) span.style.backgroundColor = tile.color;
    return span;
  }

  el2(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /**
   * One bundle's own choices: every shade (one the listing does not make in
   * this texture and length is greyed out), the lengths its shade comes in,
   * "Match the shade above" once it has its own, and Done.
   */
  editor(pick, i) {
    const box = this.el2('div', 'hq-cb__editor');
    const sh = this.shadeOf(pick);

    const f1 = this.el2('div', 'hq-cb__field');
    const l1 = this.el2('div', 'hq2-label');
    l1.appendChild(this.el2('span', '', this.t.label_shade || 'Shade'));
    l1.appendChild(this.el2('b', '', sh ? this.nice(sh.value) : ''));
    f1.appendChild(l1);
    const grid = this.el2('div', 'hq2-swatches hq-cb__swatches');
    grid.setAttribute('role', 'group');
    this.shades.forEach((s) => {
      const btn = this.el2('button', 'hq2-sw');
      btn.type = 'button';
      btn.dataset.cbPickShade = s.id;
      btn.dataset.cbPick = String(i);
      btn.setAttribute('aria-label', this.nice(s.value));
      btn.setAttribute('aria-pressed', String(!!sh && s.id === sh.id));
      /* Greyed out only when the shade is not made in this texture at all. A
         shade that does not come in this bundle's length is still a choice:
         picking it moves the bundle to the nearest length it comes in. */
      const lens = this.lengthsIn(s);
      const made = this.makesTexture(s) && (!lens || lens.length > 0);
      if (!made) { btn.disabled = true; btn.title = this.t.notMadeShort || 'Not made in this texture'; }
      btn.appendChild(this.swatch(s.value));
      grid.appendChild(btn);
    });
    f1.appendChild(grid);
    if (this.pickNote && this.pickNote.pick === pick) f1.appendChild(this.el2('p', 'hq-cb__hint hq-cb__moved', this.pickNote.text));
    box.appendChild(f1);

    const f2 = this.el2('div', 'hq-cb__field');
    const l2 = this.el2('div', 'hq2-label');
    l2.appendChild(this.el2('span', '', this.t.label_length || 'Length'));
    l2.appendChild(this.el2('b', '', this.len(pick.len)));
    f2.appendChild(l2);
    const pills = this.el2('div', 'hq-cb__pills');
    const b = sh ? this.bundles[sh.product] : null;
    this.logic.sortLengths(b ? (b.options[b.length] || []) : []).forEach((l) => {
      const v = this.variantIn(sh, l);
      const btn = this.pill(this.len(l), { cbPickLen: l, cbPick: String(i) }, v ? this.fmt(v.p) : null);
      btn.setAttribute('aria-pressed', String(this.logic.inches(l) === this.logic.inches(pick.len)));
      if (this.variants[`b${sh.product}`] && (!v || !v.a)) btn.disabled = true;
      pills.appendChild(btn);
    });
    f2.appendChild(pills);
    box.appendChild(f2);

    const foot = this.el2('div', 'hq-cb__editor-foot');
    if (pick.shade != null) {
      const reset = this.el2('button', 'hq-cb__link', this.t.matchAbove || 'Match the shade above');
      reset.type = 'button';
      reset.dataset.cbReset = String(i);
      foot.appendChild(reset);
    } else foot.appendChild(this.el2('span'));
    const done = this.el2('button', 'hq-cb__done', this.t.done || 'Done');
    done.type = 'button';
    done.dataset.cbDone = '';
    foot.appendChild(done);
    box.appendChild(foot);
    return box;
  }

  /**
   * The closure's own shade, opened from its line in "Your set": every shade,
   * greyed out where no closure or frontal is made in it (in this texture),
   * "Match the shade above" once it has its own, and Done.
   */
  closureEditor() {
    const sh = this.closureShade();
    const ed = this.el2('div', 'hq-cb__editor');
    const f = this.el2('div', 'hq-cb__field');
    const l = this.el2('div', 'hq2-label');
    l.appendChild(this.el2('span', '', this.t.closureShade || 'Closure shade'));
    l.appendChild(this.el2('b', '', sh ? this.nice(sh.value) : ''));
    f.appendChild(l);
    const grid = this.el2('div', 'hq2-swatches hq-cb__swatches');
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', this.t.closureShade || 'Closure shade');
    this.shades.forEach((s) => {
      const btn = this.el2('button', 'hq2-sw');
      btn.type = 'button';
      btn.dataset.cbCshade = s.id;
      btn.setAttribute('aria-label', this.nice(s.value));
      btn.setAttribute('aria-pressed', String(!!sh && s.id === sh.id));
      if (this.loaded && !this.types.some((t) => this.closureFor(t, s).ok)) {
        btn.disabled = true;
        btn.title = this.t.closureNoShade || 'No closure or frontal in this shade and texture';
      }
      btn.appendChild(this.swatch(s.value));
      grid.appendChild(btn);
    });
    f.appendChild(grid);
    ed.appendChild(f);
    const foot = this.el2('div', 'hq-cb__editor-foot');
    if (this.closureOwn != null) {
      const reset = this.el2('button', 'hq-cb__link', this.t.matchAbove || 'Match the shade above');
      reset.type = 'button';
      reset.dataset.cbCreset = '';
      foot.appendChild(reset);
    } else foot.appendChild(this.el2('span'));
    const done = this.el2('button', 'hq-cb__done', this.t.done || 'Done');
    done.type = 'button';
    done.dataset.cbCdone = '';
    foot.appendChild(done);
    ed.appendChild(foot);
    return ed;
  }

  drawClosure() {
    const views = this.types.map((t) => this.closureFor(t));
    let cur = views.find((v) => v.type.id === this.typeId);
    this.typeNote = '';
    if (this.loaded && cur && !cur.ok) {
      /* The chosen closure is not made in this shade or texture: take the
         first one that is, and say so. */
      const next = views.find((v) => v.ok);
      if (next) {
        this.typeNote = `${cur.type.label}: ${(this.t.closureNoTexture || 'Not made in this texture').toLowerCase()}.`;
        this.typeId = next.type.id;
        cur = next;
      }
    }

    this.el.types.innerHTML = '';
    views.forEach((v) => {
      const sub = v.variant ? this.fmt(v.variant.p) : '';
      const btn = this.pill(v.type.label, { cbType: v.type.id }, sub);
      btn.setAttribute('aria-pressed', String(v.type.id === this.typeId));
      if (this.loaded && !v.ok) {
        btn.disabled = true;
        btn.title = this.t.closureNoTexture || 'Not made in this texture';
      }
      this.el.types.appendChild(btn);
    });

    this.el.closureLengths.innerHTML = '';
    if (cur) {
      cur.lengths.forEach((l) => {
        const w = this.closureVariantAt(cur, l);
        const btn = this.pill(l, { cbClosureLength: l }, w ? this.fmt(w.p) : '');
        btn.setAttribute('aria-pressed', String(l === cur.length));
        this.el.closureLengths.appendChild(btn);
      });
    }
    this.el.closureName.textContent = cur && cur.length ? `${cur.type.label} · ${cur.length}` : (cur ? cur.type.label : '');

    const shortest = this.logic.sortLengths(this.logic.pickLengths(this.picks))[0];
    let hint = '';
    if (this.typeNote) hint = this.typeNote;
    else if (cur && cur.length && this.closureLengthPicked) hint = cur.length === this.closureLengthPicked ? (this.t.closureManual || '') : (this.t.closureNearest || '');
    else if (cur && cur.length && shortest) hint = cur.length === shortest ? (this.t.closureAuto || '') : (this.t.closureNearest || '');
    this.el.closureHint.textContent = hint;
    return cur;
  }

  closureVariantAt(view, length) {
    const c = view.product;
    const want = {};
    view.variant && view.variant.o.forEach((v, i) => { want[i] = v; });
    if (!view.variant) return null;
    want[c.length] = length;
    return this.logic.findVariant(this.variants[`c${view.type.product}`], want);
  }

  update() {
    const press = (nodes, test) => nodes.forEach((n) => n.setAttribute('aria-pressed', String(!!test(n))));

    press([...this.el.shades.children], (n) => this.shade && n.dataset.cbShade === this.shade.id);
    this.el.shadeName.textContent = this.shade ? this.nice(this.shade.value) : '';
    if (this.shade && this.texturesFor !== this.shade.product) this.drawTextures();
    if (this.textures.length > 1) {
      press([...this.el.textures.children], (n) => n.dataset.cbTexture === this.texture);
      this.el.textureName.textContent = this.nice(this.texture);
    }
    press(this.el.counts, (n) => parseInt(n.dataset.count, 10) === this.count);

    /* "Choose 3 lengths", but "Choose 1 length": {lengths} follows the count. */
    this.el.prompt.textContent = this.fill(this.t.prompt || 'Choose {count} {lengths}', {
      count: this.count,
      lengths: this.count === 1 ? 'length' : 'lengths',
    }).replace(/(\b1 )lengths\b/, '$1length');
    /* Progress as dots, one per bundle, filled as lengths are chosen, with the
       "1 of 3" beside them for anyone who reads rather than scans. */
    const counter = this.el.counter;
    counter.textContent = '';
    const dots = document.createElement('span');
    dots.className = 'hq-cb__dots';
    dots.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < this.count; i++) {
      const dot = document.createElement('i');
      if (i < this.picks.length) dot.className = 'is-on';
      dots.appendChild(dot);
    }
    counter.appendChild(dots);
    counter.appendChild(document.createTextNode(this.fill(this.t.counter || '{n} of {total}', { n: this.picks.length, total: this.count })));
    counter.classList.toggle('is-short', this.picks.length < this.count);
    this.el.lengthHint.textContent = this.t.lengthHint || '';
    this.drawLengths();

    const closure = this.drawClosure();
    this.renderTotals(closure);
  }

  renderTotals(closure) {
    const cur = closure || this.currentClosure();
    const sorted = this.logic.pickOrder(this.picks).map((i) => this.picks[i]);
    const complete = sorted.length === this.count;
    const bVars = sorted.map((p) => this.pickVariant(p));
    const cVar = cur && cur.ok ? cur.variant : null;
    const price = complete ? this.logic.priceSet(bVars, cVar) : null;
    const allIn = !!price && bVars.every((v) => v && v.a);
    const left = this.count - sorted.length;

    this.drawLines(sorted, bVars, cur, cVar);

    /* Always a number to show once loaded: the set's price when it is
       complete, "From $X" (the least it can come to) until then. */
    const est = price ? null : this.logic.priceEstimate(bVars, left, this.cheapestBundle(), cVar);
    const totalText = price ? this.fmt(price.total) : '';
    const shownText = totalText || (est ? this.fill(this.t.from || 'From {price}', { price: this.fmt(est.total) }) : '');
    this.el.total.textContent = shownText || '—';

    const shipFor = price ? price.total : (est ? est.total : null);
    this.el.ship.hidden = shipFor == null;
    if (shipFor != null) {
      const free = this.logic.shippingLine(shipFor, this.shipOpts) === 'free';
      this.el.shipLabel.textContent = free ? (this.t.shipUS || 'US shipping') : (this.t.ship || 'Shipping');
      this.el.shipValue.textContent = free ? (this.t.shipFree || 'FREE') : (this.t.shipLater || 'Calculated at checkout');
      this.el.ship.classList.toggle('is-free', free);
    }

    let label;
    if (this.failed) label = this.t.error || 'Something went wrong. Please try again.';
    else if (!this.loaded) label = this.t.loading || 'Loading…';
    else if (left > 0) label = this.fill(this.t.needMore || 'Choose {n} more {w}', { n: left, w: left === 1 ? 'length' : 'lengths' });
    else if (!allIn) label = this.t.unavailable || 'This combination is unavailable';
    else if (this.toCheckout) label = `${this.t.checkout || 'Checkout'} · ${totalText} →`;
    else label = `${this.t.add || 'Add set to cart'} · ${totalText}`;

    /* Lengths still to choose is the one gap the shopper closes on this page,
       so the button stays tappable for it — muted, aria-disabled — and a tap
       takes them to the lengths (guide) instead of doing nothing. Loading,
       a failed load or an unavailable combination still disable it outright. */
    this.pendingText = this.loaded && !this.failed && left > 0 ? label : '';
    const pending = !!this.pendingText;
    if (!this.busy) {
      this.el.addLabel.textContent = label;
      this.el.add.disabled = !allIn && !pending;
      this.el.add.classList.toggle('is-pending', pending);
      if (pending) this.el.add.setAttribute('aria-disabled', 'true');
      else this.el.add.removeAttribute('aria-disabled');
      if (this.el.addCart) this.el.addCart.hidden = !allIn;
    }
    if (!pending) this.hideNudge(true);
    this.ready = allIn;
    this.syncHost(shownText, allIn, pending, cur);
  }

  /**
   * "Your set" is where the set is checked and changed (2026-11-03: one place
   * for both, the page read twice before). One line per bundle in length
   * order and one for the closure: the shade's swatch, what it is, its shade
   * (tagged when it has its own), its price, and Change, which opens that
   * line's own shade (and a bundle's length) right there. A bundle has ✕ too.
   * A bundle still to choose is an open line that leads back to the lengths.
   */
  drawLines(sorted, bVars, cur, cVar) {
    const tex = this.nice(this.texture);
    const ul = this.el.lines;
    ul.innerHTML = '';
    const one = this.t.bundle || 'bundle';
    if (this.editing && this.picks.indexOf(this.editing) < 0) this.editing = null;
    const mixed = this.logic.mixed(this.picks, this.shade && this.shade.id) || this.closureOwn != null;
    /* Guarded: the hint is new markup, and for the seconds of a theme update
       this script can meet the previous snippet. */
    if (this.el.setHint) this.el.setHint.textContent = !this.picks.length ? '' : (mixed
      ? (this.t.setMixed || 'Lines you changed keep their own shade. The rest follow the shade above.')
      : (this.t.setHint || 'Everything comes in the shade above. Tap Change on a line to give it its own shade.'));
    this.el.clear.hidden = !this.picks.length;

    const row = (o) => {
      const li = this.el2('li', `hq-cb__line hq-cb__line--rich${o.editing ? ' is-editing' : ''}`);
      const main = this.el2('div', 'hq-cb__card-main');
      main.appendChild(this.swatch(o.shade, 'hq-cb__card-tile'));
      const text = this.el2('span', 'hq-cb__card-text');
      text.appendChild(this.el2('span', 'hq-cb__line-name', o.name));
      const sub = this.el2('span', 'hq-cb__line-sub', o.shade ? this.nice(o.shade) : '');
      if (o.own) sub.appendChild(this.el2('em', 'hq-cb__tag', this.t.ownTag || 'Own shade'));
      text.appendChild(sub);
      if (o.alert) text.appendChild(this.el2('span', 'hq-cb__card-alert', o.alert));
      main.appendChild(text);
      const side = this.el2('span', 'hq-cb__card-side');
      side.appendChild(this.el2('span', 'hq-cb__line-price', o.cents == null ? '' : this.fmt(o.cents)));
      const acts = this.el2('span', 'hq-cb__card-actions');
      const edit = this.el2('button', 'hq-cb__link', o.editing ? (this.t.close || 'Close') : (this.t.change || 'Change'));
      edit.type = 'button';
      Object.assign(edit.dataset, o.editData);
      edit.setAttribute('aria-expanded', String(!!o.editing));
      edit.setAttribute('aria-label', `${o.editing ? 'Close' : 'Change'} ${o.label}`);
      acts.appendChild(edit);
      if (o.dropData) {
        const x = this.el2('button', 'hq-cb__x', '✕');
        x.type = 'button';
        Object.assign(x.dataset, o.dropData);
        x.setAttribute('aria-label', `Remove ${o.label}`);
        acts.appendChild(x);
      }
      side.appendChild(acts);
      main.appendChild(side);
      li.appendChild(main);
      if (o.editing) li.appendChild(o.editor());
      ul.appendChild(li);
    };

    for (let n = 0; n < this.count; n++) {
      const name = this.fill(this.t.lineBundle || 'Bundle {n}', { n: n + 1 });
      const pick = sorted[n];
      if (!pick) {
        const li = this.el2('li', 'hq-cb__line is-open');
        const open = this.el2('button', 'hq-cb__line-main');
        open.type = 'button';
        open.dataset.cbSlot = '';
        open.appendChild(this.el2('span', 'hq-cb__line-name', `${name}: ${this.t.lineChoose || 'choose a length'}`));
        li.appendChild(open);
        ul.appendChild(li);
        continue;
      }
      const i = this.picks.indexOf(pick);
      const sh = this.shadeOf(pick);
      const v = bVars[n];
      row({
        name: `${name}: ${this.len(pick.len)}${tex ? ` ${tex}` : ''}`,
        shade: sh ? sh.value : '',
        own: pick.shade != null,
        cents: v ? v.p : null,
        alert: this.loaded && (!v || !v.a) ? (this.t.notMade || 'Not made in this texture and length. Change it or remove it.') : '',
        editing: this.editing === pick,
        editData: { cbEdit: String(i) },
        dropData: { cbDrop: String(i) },
        label: `${one} ${n + 1}`,
        editor: () => this.editor(pick, i),
      });
    }
    if (cur && cur.length) {
      const csh = this.closureShade();
      row({
        name: `${cur.type.label}: ${this.len(cur.length)}${tex ? ` ${tex}` : ''}`,
        shade: csh ? csh.value : '',
        own: this.closureOwn != null,
        cents: cVar ? cVar.p : null,
        editing: !!this.closureEditing,
        editData: { cbCedit: '' },
        label: 'the closure shade',
        editor: () => this.closureEditor(),
      });
    }
  }

  /**
   * Show the shopper what is missing: scroll the lengths into view, warm them
   * with a short glow, and say in a soft note how many are left. Nothing red,
   * nothing that stays: the glow fades in about two seconds and the note in
   * about five, or as soon as the set is complete.
   */
  guide() {
    const group = this.el.lengths.closest('.hq-cb__group');
    const left = this.count - this.picks.length;
    if (!group || left <= 0) return;
    const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    group.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });

    group.classList.remove('is-guide');
    void group.offsetWidth; // restart the glow on a second tap
    group.classList.add('is-guide');
    clearTimeout(this.glowTimer);
    this.glowTimer = setTimeout(() => group.classList.remove('is-guide'), 2600);

    const w = left === 1 ? 'length' : 'lengths';
    const tpl = this.t.nudge || (this.picks.length
      ? 'Tap {n} more {w} above to finish your set.'
      : 'Tap {n} {w} above. Tap one twice for two of the same.');
    const nudge = this.el.nudge;
    nudge.textContent = this.fill(tpl, { n: left, w });
    nudge.hidden = false;
    requestAnimationFrame(() => nudge.classList.add('is-on'));
    clearTimeout(this.nudgeTimer);
    this.nudgeTimer = setTimeout(() => this.hideNudge(), 5000);

    /* Focus follows for keyboard and screen-reader users, after the scroll. */
    const first = this.el.lengths.querySelector('button:not([disabled])');
    if (first) setTimeout(() => first.focus({ preventScroll: true }), still ? 0 : 450);
  }

  hideNudge(now) {
    const nudge = this.el.nudge;
    if (!nudge || nudge.hidden) return;
    clearTimeout(this.nudgeTimer);
    nudge.classList.remove('is-on');
    if (now) { nudge.hidden = true; return; }
    setTimeout(() => { if (!nudge.classList.contains('is-on')) nudge.hidden = true; }, 350);
  }

  /** Gallery follows the shade; the sticky bar quotes the set total. */
  syncHost(totalText, available, pending, closure) {
    const host = this.pdp;
    if (!host) return;
    if (this.shade && this.shade.value !== this.hostShade) {
      if (typeof host.filterByShade === 'function') {
        /* The first call is the landing shade: the general cover photo leads.
           Every later one is the customer's pick: that shade's photos first. */
        const picked = this.hostShade !== undefined;
        this.hostShade = this.shade.value;
        host.filterByShade(this.shade.value, picked);
      } else if (!this.waitingForHost) {
        this.waitingForHost = true;
        customElements.whenDefined('hq2-pdp').then(() => { this.waitingForHost = false; this.update(); });
      }
    }
    host.querySelectorAll('[data-hq2-sticky-price], [data-hq2-sticky-btn-price]')
      .forEach((n) => { n.textContent = totalText; });
    const meta = host.querySelector('[data-hq2-sticky-meta]');
    /* While lengths are missing the bar says so, and its button stays live:
       it calls addToCart(), which guides instead of adding. */
    if (meta) meta.textContent = pending ? this.pendingText : (this.bar ? this.barMeta(closure) : this.setLabel());
    const btn = host.querySelector('[data-hq2-sticky-atc]');
    if (btn) btn.disabled = !available && !pending;
    host.querySelectorAll('[data-hq2-sticky-btn-price], .hq2-sticky-btn-sep').forEach((n) => { n.hidden = !available; });
  }

  /** The bar's line under the price: "3 bundles + 2x6 Closure · Smoked Brown Blend". */
  barMeta(closure) {
    const parts = [`${this.count} ${this.word(this.count)}${closure ? ` + ${closure.type.label}` : ''}`];
    if (this.logic.mixed(this.picks, this.shade && this.shade.id) || this.closureOwn != null) parts.push(this.t.mixedShades || 'Mixed shades');
    else if (this.shade) parts.push(this.nice(this.shade.value));
    return parts.join(' · ');
  }

  /** Called by the host after its own sticky render, so it never shows the page product's price. */
  syncHostChrome() { if (this.el) this.renderTotals(); }

  setLabel() {
    /* {w} is "bundle" or "bundles" by the count; a saved label that hard-codes
       "bundles" is still made singular for a set of one. */
    return this.fill(this.t.setLabel || '{n} {w} + closure', { n: this.count, w: this.word(this.count) })
      .replace(/(^|\D)1 bundles\b/, '$11 bundle');
  }

  /* ------------------------------------------------------------------ input */

  bind() {
    this.addEventListener('click', (e) => {
      const t = e.target.closest('button');
      if (!t || !this.contains(t) || t.disabled) return;
      const d = t.dataset;
      if (d.cbShade != null) {
        const next = this.shades.find((sh) => sh.id === d.cbShade);
        if (!next) return;
        const moved = !this.shade || next.product !== this.shade.product;
        this.shade = next;
        if (moved) {
          /* Another listing: keep the texture if it sells it, keep every
             picked length it sells (by inches), drop the rest. */
          this.texture = this.logic.sameValue(this.textures, this.texture) || this.textures[0] || null;
          /* Bundles that follow the set move with it; a bundle with its own
             shade stays in its own listing. */
          this.picks = this.picks.map((p) => {
            if (p.shade != null) return p;
            const len = this.logic.carryLengths([p.len], this.bundleLengths)[0];
            return len ? this.logic.makePick(len, null) : null;
          }).filter(Boolean);
        }
        /* A bundle or closure given this very shade simply follows the set again. */
        this.picks.forEach((p) => { if (p.shade === next.id) p.shade = null; });
        if (this.closureOwn === next.id) this.closureOwn = null;
        this.keepOwnShades();
      }
      else if (d.cbTexture != null) { this.texture = d.cbTexture; this.keepOwnShades(); }
      else if (d.count != null) {
        const n = parseInt(d.count, 10);
        if (n === this.count) return;
        this.count = n;
        this.picks = this.logic.fitToCount(this.picks, n);
      } else if (d.cbLength != null) this.picks = this.logic.tapPick(this.picks, d.cbLength, this.count);
      else if (d.cbDrop != null) this.picks = this.logic.removePickAt(this.picks, parseInt(d.cbDrop, 10));
      else if (d.cbEdit != null) {
        this.pickNote = null;
        const p = this.picks[parseInt(d.cbEdit, 10)];
        this.editing = this.editing === p ? null : p;
      } else if (d.cbPickShade != null) {
        const p = this.picks[parseInt(d.cbPick, 10)];
        if (!p) return;
        p.shade = this.shade && d.cbPickShade === this.shade.id ? null : d.cbPickShade;
        this.pickNote = null;
        const sh = this.shadeOf(p);
        const lens = this.lengthsIn(sh);
        const v = this.variantIn(sh, p.len);
        if (lens && lens.length && !(v && v.a)) {
          const was = this.len(p.len);
          p.len = this.logic.closureLengthFor([p.len], lens, null);
          const longer = this.logic.inches(was) > this.logic.inches(lens[lens.length - 1]);
          const tpl = longer
            ? (this.t.movedDown || '{shade} comes in up to {edge}, so this bundle is now {len} (was {was}).')
            : (this.t.movedUp || '{shade} starts at {edge}, so this bundle is now {len} (was {was}).');
          this.pickNote = {
            pick: p,
            text: this.fill(tpl, { shade: this.nice(sh.value), edge: this.len(longer ? lens[lens.length - 1] : lens[0]), len: this.len(p.len), was }),
          };
        }
      } else if (d.cbPickLen != null) {
        this.pickNote = null;
        const p = this.picks[parseInt(d.cbPick, 10)];
        if (!p) return;
        p.len = d.cbPickLen;
      } else if (d.cbReset != null) {
        const p = this.picks[parseInt(d.cbReset, 10)];
        if (!p) return;
        p.shade = null;
        /* Back to the set's listing: the length as that listing spells it,
           or the bundle goes if the set's shade does not come in it. */
        const len = this.logic.carryLengths([p.len], this.bundleLengths)[0];
        if (len) p.len = len;
        else this.picks = this.picks.filter((x) => x !== p);
      } else if (d.cbDone != null) { this.editing = null; this.pickNote = null; }
      else if (d.cbCedit != null) this.closureEditing = !this.closureEditing;
      else if (d.cbCshade != null) this.closureOwn = this.shade && d.cbCshade === this.shade.id ? null : d.cbCshade;
      else if (d.cbCreset != null) this.closureOwn = null;
      else if (d.cbCdone != null) this.closureEditing = false;
      else if (t.hasAttribute('data-clear')) { this.picks = []; this.editing = null; }
      else if (d.cbType != null) this.typeId = d.cbType;
      else if (d.cbClosureLength != null) this.closureLengthPicked = d.cbClosureLength;
      else if (t.hasAttribute('data-add')) { this.addToCart(); return; }
      else if (t.hasAttribute('data-add-cart')) { this.addToCart('cart'); return; }
      else if (d.cbSlot != null) { this.guide(); return; }
      else return;
      this.update();
    });
  }

  /**
   * After the texture or the set's shade changes: a bundle whose own shade is
   * not made in the texture any more goes back to following the set (as the
   * set's listing spells its length, or out of the set if that listing does
   * not make the length at all).
   */
  keepOwnShades() {
    this.picks = this.picks.map((p) => {
      if (p.shade == null) return p;
      const sh = this.shades.find((s) => s.id === p.shade);
      if (sh && this.makesTexture(sh)) return p;
      const len = this.logic.carryLengths([p.len], this.bundleLengths)[0];
      return len ? this.logic.makePick(len, null) : null;
    }).filter(Boolean);
  }

  /* ------------------------------------------------------------------- cart */

  /**
   * Adds the set, then goes where `dest` says: 'checkout' (the main button and
   * the sticky bar, when the block sends them there), 'cart', or '' to stay.
   */
  addToCart(dest = this.toCheckout ? 'checkout' : (this.cartRedirect ? 'cart' : '')) {
    if (this.busy) return;
    if (!this.ready) { if (this.pendingText) this.guide(); return; }
    const sorted = this.logic.pickOrder(this.picks).map((i) => this.picks[i]);
    const bVars = sorted.map((p) => this.pickVariant(p));
    const cur = this.currentClosure();
    if (bVars.some((v) => !v || !v.a) || !cur || !cur.ok) return;

    const items = this.logic.buildCartItems({
      bundleIds: bVars.map((v) => v.id),
      closureId: cur.variant.id,
      comboId: this.logic.makeComboId(),
      label: this.setLabel(),
      /* Only when the closure listing has no texture of its own to say it. */
      texture: cur.product.texture > -1 ? '' : this.nice(this.texture),
    });

    this.busy = true;
    this.el.add.disabled = true;
    this.el.add.classList.add('is-busy');
    if (this.el.addCart) this.el.addCart.hidden = true;
    this.el.addLabel.textContent = dest === 'checkout' ? (this.t.going || 'Going to checkout…') : (this.t.adding || 'Adding…');
    this.el.status.hidden = true;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || data.message || 'Add to cart failed');
        document.dispatchEvent(new CustomEvent('hq:combo:added', { detail: { items } }));
        if (dest) { window.location.href = `${root}${dest}`; return; }
        this.busy = false;
        this.el.add.classList.remove('is-busy');
        this.status(`${sorted.length} ${this.word(sorted.length)} + 1 ${cur.type.label} added to your cart.`, true);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hq-combo-builder', cartData: data });
        }
        this.renderTotals();
      })
      .catch((err) => {
        console.error('[hq-combo-builder]', err);
        this.busy = false;
        this.el.add.classList.remove('is-busy');
        this.status(err.message || this.t.error, false);
        this.renderTotals();
      });
  }

  status(text, ok) {
    const el = this.el.status;
    el.textContent = text;
    el.classList.toggle('is-ok', !!ok);
    el.hidden = false;
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => { el.hidden = true; }, 6000);
  }
}

if (!customElements.get('hq-combo-builder')) {
  customElements.define('hq-combo-builder', HqComboBuilder);
}
